import React, { useState } from 'react';
import { resolveIcon } from '../common/IconResolver';
import { ChevronDownIcon, ChevronRightIcon } from '../common/Icons';

export interface NavTreeItem {
  id: string;
  label: string;
  icon?: string;
  view?: string;
}

export interface NavTreeGroup {
  id: string;
  label: string;
  icon?: string;
  items: NavTreeItem[];
}

interface NavTreePreviewProps {
  groups: NavTreeGroup[];
  stubMetadata?: Record<string, { title?: string; expectedPhase?: string }>;
}

const NavTreePreview: React.FC<NavTreePreviewProps> = ({ groups, stubMetadata }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(groups.map(g => g.id)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  if (groups.length === 0) {
    return (
      <div
        data-testid="nav-tree-preview-empty"
        style={{
          padding: '24px',
          textAlign: 'center',
          color: 'var(--text-secondary)',
          fontSize: '13px',
        }}
      >
        No navigation groups defined.
      </div>
    );
  }

  return (
    <div data-testid="nav-tree-preview">
      {/* Expand/Collapse controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        marginBottom: '8px',
      }}>
        <button
          onClick={expandAll}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            fontSize: '11px',
            cursor: 'pointer',
            padding: '2px 6px',
          }}
        >
          Expand All
        </button>
        <button
          onClick={collapseAll}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            fontSize: '11px',
            cursor: 'pointer',
            padding: '2px 6px',
          }}
        >
          Collapse All
        </button>
      </div>

      {/* Groups */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {groups.map(group => {
          const isExpanded = expandedGroups.has(group.id);
          return (
            <div key={group.id} data-testid={`nav-group-${group.id}`}>
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isExpanded}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 10px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>
                  {resolveIcon(group.icon, 16)}
                </span>
                <span style={{ flex: 1, textAlign: 'left' }}>{group.label}</span>
                <span className="badge badge-secondary" style={{ fontSize: '0.6rem', padding: '2px 6px' }}>
                  {group.items.length}
                </span>
                <span style={{ color: 'var(--text-tertiary)' }}>
                  {isExpanded ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
                </span>
              </button>

              {/* Group Items */}
              {isExpanded && (
                <div style={{
                  paddingLeft: '20px',
                  marginTop: '2px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1px',
                }}>
                  {group.items.map(item => {
                    const isRealView = item.view && item.view !== 'stub';
                    const meta = stubMetadata?.[item.id] || stubMetadata?.[item.view || ''];
                    return (
                      <div
                        key={item.id}
                        data-testid={`nav-item-${item.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          color: 'var(--text-secondary)',
                          borderRadius: '6px',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>
                          {resolveIcon(item.icon, 14)}
                        </span>
                        <span style={{ flex: 1 }}>{item.label}</span>
                        {isRealView ? (
                          <span
                            className="badge badge-success"
                            style={{ fontSize: '0.55rem', padding: '1px 5px' }}
                          >
                            Real View
                          </span>
                        ) : (
                          <span
                            className="badge badge-secondary"
                            style={{ fontSize: '0.55rem', padding: '1px 5px' }}
                          >
                            Stub
                          </span>
                        )}
                        {meta?.expectedPhase && (
                          <span style={{
                            fontSize: '0.55rem',
                            color: 'var(--text-tertiary)',
                            background: 'rgba(255, 255, 255, 0.05)',
                            padding: '1px 5px',
                            borderRadius: '4px',
                          }}>
                            {meta.expectedPhase}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NavTreePreview;
